/**
 * Shared cross-facility navigation helpers.
 *
 * Used by nodeControls.vue and TagCard.vue to keep click-behavior
 * classification and cross-facility menu construction in one place.
 * Values must match the raw strings that templates still compare against
 * (e.g. "navigate-menu") — renaming a value requires updating every
 * template v-bind that references it.
 */

import { resolveTagSpatialIntent } from '@/features/Tags/utils/tagSpatialNavigation';

export const CLICK_BEHAVIOR = Object.freeze({
  LOCAL: 'local',                  // Use the local viewer (3D isolate, 360 show, 2D show, zoom)
  ZOOM_SCAN: 'zoom-scan',          // 360-scan-specific: zoom to positioned scan
  NAVIGATE_SINGLE: 'navigate-single',
  NAVIGATE_MENU: 'navigate-menu',
  NONE: 'none',
});

function facilitiesFromPlan(plan, viewer, facilityDict) {
  const candidates = plan.kind === 'navigate-facility' ? [plan] : plan.alternatives;
  const ids = new Set((candidates || [])
    .filter((candidate) => candidate.viewer === viewer)
    .map((candidate) => candidate.facilityId));
  return [...ids].map((id) => ({
    id,
    name: facilityDict[id] || id,
  }));
}

const hasSpatialTargetsContract = tag => Object.prototype.hasOwnProperty.call(
  tag || {},
  'spatialTargets',
);

const normalizedId = value => (
  typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : null
);

const facilityName = (facilityId, facility) => (
  typeof facility === 'string' ? facility : (facility?.name || facilityId)
);

/**
 * Recover the historical remote-harvest menu only for old API responses that
 * omit the spatialTargets contract. The facility dictionary is the UI's
 * already access-filtered catalogue, so advertised ids outside it stay hidden.
 */
export function buildAdvertisedRemoteHarvestFacilities(
  tag,
  facilityDict = {},
  currentFacilityId = null,
) {
  const currentId = normalizedId(currentFacilityId);
  const advertisedIds = new Set((Array.isArray(tag?.otherAvailableAssets)
    ? tag.otherAvailableAssets
    : []).map(normalizedId).filter(Boolean));
  const seen = new Set();
  return Object.entries(facilityDict || {}).reduce((facilities, [facilityId, facility]) => {
      const normalizedFacilityId = normalizedId(facilityId);
      const include = normalizedFacilityId
        && normalizedFacilityId !== currentId
        && advertisedIds.has(normalizedFacilityId)
        && !seen.has(normalizedFacilityId);
      if (include) {
        seen.add(normalizedFacilityId);
        facilities.push({ id: facilityId, name: facilityName(facilityId, facility) });
      }
      return facilities;
    }, []);
}

export function buildLegacyRemoteHarvestFacilities(
  tag,
  facilityDict = {},
  currentFacilityId = null,
) {
  if (hasSpatialTargetsContract(tag) || tag?.isMaster !== true) return [];
  return buildAdvertisedRemoteHarvestFacilities(tag, facilityDict, currentFacilityId);
}

/**
 * Is this tag harvested — does it carry model elements — in ANY facility?
 *
 * `availableAssets` cannot answer that any more: the API narrowed `Children` to the
 * current facility, so the array it is derived from (forgeModels.js
 * processChildrenForFacilityGroup) can only ever name the current facility. The
 * cross-facility answer now lives in the authoritative spatialTargets contract, which
 * carries one target per facility with its own `hasModelElements` flag.
 *
 * Deliberately NOT the same question as "is there a 3D destination": a master placed by
 * point but never harvested has a target and no model elements, and must keep reading as
 * unharvested. Responses that predate the contract keep their historical answer.
 *
 * @param {Object} tag - Hydrated tag, ideally with spatialTargets
 * @returns {boolean}
 */
export function hasHarvestInAnyFacility(tag) {
  if (!hasSpatialTargetsContract(tag)) {
    return (tag?.availableAssets || []).length > 0;
  }
  return (Array.isArray(tag.spatialTargets) ? tag.spatialTargets : [])
    .some((target) => target?.hasModelElements === true);
}

/**
 * Facilities OTHER than the current one where this master is actually harvested.
 *
 * The harvest-dot tooltip asks a HARVEST question, so it cannot reuse
 * `buildOther3dFacilities`: that one answers "where can I go and see this in 3D", and a
 * facility where the master was only Set-Positioned is a valid 3D destination
 * (`provenance: 'point-3d'`, `hasModelElements: false`) without being a harvest. Placement
 * in a facility that has no harvest is exactly what this branch now allows, so naming those
 * facilities under an "Available in" harvest label would contradict the rule.
 *
 * Responses that predate the contract keep their historical advertised answer, the same
 * fallback `hasHarvestInAnyFacility` uses.
 *
 * @param {Object} tag - Hydrated tag, ideally with spatialTargets
 * @param {Object} facilityDict - Map of facilityId -> displayName
 * @param {string} currentFacilityId - Facility currently open in the UI
 * @returns {Array<{id: string, name: string}>}
 */
export function buildOtherHarvestedFacilities(
  tag,
  facilityDict = {},
  currentFacilityId = null,
) {
  if (!hasSpatialTargetsContract(tag)) {
    return buildAdvertisedRemoteHarvestFacilities(tag, facilityDict, currentFacilityId);
  }
  const currentId = normalizedId(currentFacilityId);
  const ids = new Set((Array.isArray(tag.spatialTargets) ? tag.spatialTargets : [])
    .filter((target) => target?.hasModelElements === true)
    .map((target) => target.facilityId)
    .filter((id) => normalizedId(id) && normalizedId(id) !== currentId));
  return [...ids].map((id) => ({ id, name: facilityName(id, facilityDict[id]) }));
}

/**
 * Adapt the resolver's authorized 360 alternatives into the existing
 * deduplicated {id, name} menu shape.
 *
 * The resolver is the authority for which destination facilities are valid;
 * this helper only adapts its plan to the existing menu shape.
 *
 * @param {Object} tag - Hydrated tag with spatialTargets
 * @param {Object} facilityDict - Map of facilityId → displayName
 * @param {string} currentFacilityId - Facility currently open in the UI
 * @returns {Array<{id: string, name: string}>}
 */
export function buildOther360Facilities(tag, facilityDict = {}, currentFacilityId = null) {
  const plan = resolveTagSpatialIntent(tag, {
    currentFacilityId,
    intent: 'copy-link',
  });
  return facilitiesFromPlan(plan, '360', facilityDict);
}

/**
 * Whether a tag can sit in several facilities, so its buttons follow the resolver's spatial plan
 * (orange, right-click facilities, navigate): a master, or a tag owned by a facility group (AVA-796).
 * A facility-owned manual tag lives in one facility only.
 */
export function followsSpatialPlan(tag) {
  return !!tag && (!!tag.isMaster || !!tag.facilityGroupId);
}

export function buildOther3dFacilities(tag, facilityDict = {}, currentFacilityId = null) {
  const legacyFacilities = buildLegacyRemoteHarvestFacilities(
    tag,
    facilityDict,
    currentFacilityId,
  );
  if (legacyFacilities.length > 0) return legacyFacilities;

  const plan = resolveTagSpatialIntent(tag, {
    currentFacilityId,
    intent: 'isolate-3d',
  });
  return facilitiesFromPlan(plan, '3d', facilityDict);
}

/**
 * Classify a resolver plan into the existing button behavior vocabulary.
 * Callers can supply current-facility evidence needed by legacy API shapes.
 *
 * Caller is responsible for any type guard (e.g. instanceof ForgeBaseTag)
 * before calling so this helper does not import the forgeModels tree.
 *
 * @param {Object} item - Hydrated tag with spatialTargets
 * @param {string} currentFacilityId - Facility currently open in the UI
 * @param {string} intent - Resolver intent for this control
 * @param {Object} context - Additional resolver evidence such as currentFacilityImageIds
 * @returns {string} One of CLICK_BEHAVIOR.{LOCAL, NAVIGATE_SINGLE, NAVIGATE_MENU, NONE}
 */
export function computeSimpleClickBehavior(
  item,
  currentFacilityId = null,
  intent = 'isolate-3d',
  context = {},
) {
  const plan = resolveTagSpatialIntent(item, { ...context, currentFacilityId, intent });
  if (plan.kind === 'viewer') return CLICK_BEHAVIOR.LOCAL;
  if (plan.kind === 'navigate-facility') return CLICK_BEHAVIOR.NAVIGATE_SINGLE;
  if (plan.kind === 'choose-facility') return CLICK_BEHAVIOR.NAVIGATE_MENU;

  if (intent === 'isolate-3d'
    && !hasSpatialTargetsContract(item)
    && item?.isMaster === true) {
    const remoteFacilities = buildLegacyRemoteHarvestFacilities(
      item,
      context.facilityDict,
      currentFacilityId,
    );
    if (remoteFacilities.length === 1) return CLICK_BEHAVIOR.NAVIGATE_SINGLE;
    if (remoteFacilities.length > 1) return CLICK_BEHAVIOR.NAVIGATE_MENU;
  }
  return CLICK_BEHAVIOR.NONE;
}

export const TAG_3D_LABELS = Object.freeze({
  ISOLATE: 'Isolate 3D Viewer',
  SHOW_POINT: 'Show in 3D',
  ZOOM: 'Zoom to tag in 3D',
});

// An anomaly's 3D scene is owned by its spatial-focus composable, which only runs on isolate
// (forgeService.zoomInAllAvailableViewers). A plain zoom would take the generic children path
// that composable exists to replace, so anomalies are not offered one.
const ZOOM_EXCLUDED_TAG_TYPES = Object.freeze(['anomaly']);

function tagTypeToken(tag) {
  const type = typeof tag?.getType === 'function' ? tag.getType() : tag?.type;
  return typeof type === 'string' ? type.trim().toLowerCase() : '';
}

/**
 * Does the tag carry something the 3D viewer can isolate, rather than only a point to frame?
 *
 * Responses that predate the spatialTargets contract report true: they keep their historical
 * isolate behaviour and never enter the point-only mode.
 *
 * @param {Object} tag - Hydrated tag
 * @param {Object} [options]
 * @param {boolean} [options.canIsolate] - The surface declared the element isolatable without
 *   advertised 3D targets (TagCard's `canIsolate` prop).
 * @returns {boolean}
 */
export function hasDirect3dIsolateTarget(tag, { canIsolate = false } = {}) {
  const spatialTargets = tag?.spatialTargets;
  if (!Array.isArray(spatialTargets)) return true;
  return canIsolate || spatialTargets.some((target) => target?.viewer === '3d');
}

/**
 * The 3D button's single decision, shared by every surface that shows one (360 popup, Tags
 * Register, tag card) so visibility, label, click and right-click menu come from one answer.
 *
 * - A tag with model elements here isolates them on click; framing it without isolating is
 *   offered from the right-click menu (`canZoomFromMenu`).
 * - A tag placed from a 360 scan has only a point, so the click itself frames that point
 *   (`pointOnly`) and there is no zoom item to offer.
 *
 * @param {Object} tag - Hydrated tag with spatialTargets
 * @param {string} currentFacilityId - Facility currently open in the UI
 * @param {Object} [options]
 * @param {Object} [options.facilityDict] - Map of facilityId -> displayName
 * @param {boolean} [options.canIsolate] - See hasDirect3dIsolateTarget
 * @returns {{behavior: string, pointOnly: boolean, canZoomFromMenu: boolean, localLabel: string}}
 */
export function resolveTag3dButton(
  tag,
  currentFacilityId = null,
  { facilityDict = {}, canIsolate = false } = {},
) {
  const behavior = computeSimpleClickBehavior(
    tag,
    currentFacilityId,
    'isolate-3d',
    { facilityDict, allowPointOnly3d: true },
  );
  const isLocal = behavior === CLICK_BEHAVIOR.LOCAL;
  const pointOnly = isLocal && !hasDirect3dIsolateTarget(tag, { canIsolate });
  return Object.freeze({
    behavior,
    pointOnly,
    canZoomFromMenu: isLocal && !pointOnly && !ZOOM_EXCLUDED_TAG_TYPES.includes(tagTypeToken(tag)),
    localLabel: pointOnly ? TAG_3D_LABELS.SHOW_POINT : TAG_3D_LABELS.ISOLATE,
  });
}

/**
 * The NAVIGATE_TAG_SPATIALLY fields that go with a resolveTag3dButton decision.
 *
 * `allowPointOnly3d` is always sent so the click resolves exactly the plan the button was shown
 * for. `isolate` is false for a point-only tag (isolating an empty element set greys the whole
 * model) and for the explicit zoom.
 *
 * @param {Object} decision - Result of resolveTag3dButton
 * @param {Object} [options]
 * @param {boolean} [options.zoom] - The user chose "Zoom to tag in 3D"
 * @returns {{allowPointOnly3d: boolean, isolate: boolean}}
 */
export function tag3dNavigationFields(decision, { zoom = false } = {}) {
  return {
    allowPointOnly3d: true,
    isolate: !zoom && decision?.pointOnly !== true,
  };
}
